import type { UUID } from "utils";

const BASE_URL = "/stablecoins";
const WALLETS_URL = `${BASE_URL}/wallets`;
const BENEFICIARIES_URL = `${BASE_URL}/beneficiaries`;
const SETTLEMENT_DESTINATIONS_URL = `${BASE_URL}/settlement-destinations`;
const SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL = `${SETTLEMENT_DESTINATIONS_URL}/bank-accounts`;
const SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL = `${SETTLEMENT_DESTINATIONS_URL}/crypto-wallets`;
const PAYMENTS_URL = `${BASE_URL}/payments`;
const PAYMENTS_TRANSFERS_URL = `${PAYMENTS_URL}/transfers`;
const PAYMENTS_SETTLEMENTS_URL = `${PAYMENTS_URL}/settlements`;

export const URL_PARAMS = {
  CONFIGURE: "configure",
} as const;

export const CONFIGURE_PARAM_VALUES = {
  DESTINATION: "destination",
  RESERVE: "reserve",
  SCHEDULE: "schedule",
} as const;

export const PAGE_URLS = {
  onboarding: `${BASE_URL}/onboarding`,

  // Wallets — list with Fiat / Crypto tabs
  wallets: WALLETS_URL,
  walletsFiat: `${WALLETS_URL}/fiat`,
  walletsCrypto: `${WALLETS_URL}/crypto`,
  walletsNew: `${WALLETS_URL}/new`,
  walletDetails: `${WALLETS_URL}/:walletId`,
  walletSettlementSettings: `${WALLETS_URL}/:walletId/settlement`,
  walletSettlementConfigure: `${WALLETS_URL}/:walletId/settlement/configure`,
  walletRunSettlement: `${WALLETS_URL}/:walletId/run-settlement`,

  // Beneficiaries — standalone section
  beneficiaries: BENEFICIARIES_URL,
  beneficiaryNew: `${BENEFICIARIES_URL}/new`,
  beneficiaryEdit: `${BENEFICIARIES_URL}/:beneficiaryId/edit`,

  // Settlement destinations — standalone section (bank accounts + crypto wallets)
  settlementDestinations: SETTLEMENT_DESTINATIONS_URL,
  settlementDestinationsBankAccounts: SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL,
  settlementDestinationsCryptoWallets:
    SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL,
  bankAccountNew: `${SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL}/new`,
  bankAccountEdit: `${SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL}/:bankAccountId/edit`,
  cryptoWalletNew: `${SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL}/new`,
  cryptoWalletEdit: `${SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL}/:cryptoWalletId/edit`,

  // Payments — unified money-movement list (payment / transfer / payout /
  // settlement / refund) with transfer + settlement details nested under it.
  payments: PAYMENTS_URL,
  paymentsTransfer: `${PAYMENTS_URL}/transfer`,
  paymentsTransferDetails: `${PAYMENTS_TRANSFERS_URL}/:transferId`,
  paymentsSettlementDetails: `${PAYMENTS_SETTLEMENTS_URL}/:settlementId`,
  paymentDetails: `${PAYMENTS_URL}/:id`,

  getLinkToPaymentDetails: (fiatTxId: UUID) => `${PAYMENTS_URL}/${fiatTxId}`,
  getLinkToTransferDetails: (transferId: UUID) =>
    `${PAYMENTS_TRANSFERS_URL}/${transferId}`,
  getLinkToSettlementDetails: (settlementId: UUID) =>
    `${PAYMENTS_SETTLEMENTS_URL}/${settlementId}`,
  getLinkToBeneficiaryEdit: (beneficiaryId: UUID) =>
    `${BENEFICIARIES_URL}/${beneficiaryId}/edit`,
  getLinkToBankAccountEdit: (bankAccountId: UUID) =>
    `${SETTLEMENT_DESTINATIONS_BANK_ACCOUNTS_URL}/${bankAccountId}/edit`,
  getLinkToCryptoWalletEdit: (cryptoWalletId: UUID) =>
    `${SETTLEMENT_DESTINATIONS_CRYPTO_WALLETS_URL}/${cryptoWalletId}/edit`,
  getLinkToWalletDetails: (walletId: UUID) => `${WALLETS_URL}/${walletId}`,
  getLinkToWalletSettlementSettings: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement`,
  getLinkToWalletRunSettlement: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/run-settlement`,
  getLinkToWalletSettlementConfigure: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement/configure`,
  getLinkToWalletSettlementReserve: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement?${URL_PARAMS.CONFIGURE}=${CONFIGURE_PARAM_VALUES.RESERVE}`,
  getLinkToWalletSettlementSchedule: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement?${URL_PARAMS.CONFIGURE}=${CONFIGURE_PARAM_VALUES.SCHEDULE}`,
  getLinkToWalletSettlementDestination: (walletId: UUID) =>
    `${WALLETS_URL}/${walletId}/settlement?${URL_PARAMS.CONFIGURE}=${CONFIGURE_PARAM_VALUES.DESTINATION}`,
} as const;
